/**
 * Client-side folder color tags (macOS Finder style).
 * Stored in localStorage keyed by asset id. Backend is unaware.
 */
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'drive_folder_colors_v1';
const EVENT       = 'drive-folder-colors-change';

export const FOLDER_COLORS = [
  { id: 'default', label: 'None',   icon: '#2563eb', tint: '#eff6ff', border: '#dbeafe' },
  { id: 'red',     label: 'Red',    icon: '#dc2626', tint: '#fef2f2', border: '#fecaca' },
  { id: 'orange',  label: 'Orange', icon: '#ea580c', tint: '#fff7ed', border: '#fed7aa' },
  { id: 'yellow',  label: 'Yellow', icon: '#ca8a04', tint: '#fefce8', border: '#fde68a' },
  { id: 'green',   label: 'Green',  icon: '#16a34a', tint: '#f0fdf4', border: '#bbf7d0' },
  { id: 'blue',    label: 'Blue',   icon: '#2563eb', tint: '#eff6ff', border: '#bfdbfe' },
  { id: 'purple',  label: 'Purple', icon: '#9333ea', tint: '#faf5ff', border: '#e9d5ff' },
  { id: 'pink',    label: 'Pink',   icon: '#db2777', tint: '#fdf2f8', border: '#fbcfe8' },
  { id: 'gray',    label: 'Gray',   icon: '#475569', tint: '#f1f5f9', border: '#cbd5e1' },
];

/**
 * Unified default folder color (app blue) — every folder the user hasn't
 * explicitly customised shows the SAME color for a consistent look. Users can
 * still pick a per-folder color via DriveFolderForm, which overrides this.
 */
export const DEFAULT_COLOR_ID = 'default';

/**
 * Default color for an asset id. Now unified (ignores the id) so all
 * uncustomised folders render one color. Signature kept for callers.
 */
export const getDefaultColorForAsset = () =>
  FOLDER_COLORS.find((c) => c.id === DEFAULT_COLOR_ID) || FOLDER_COLORS[0];

const readAll = () => {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeAll = (data) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch {
    /* quota / disabled — ignore */
  }
};

export const getFolderColorId = (assetId) => {
  if (!assetId) return 'default';
  const all = readAll();
  return all[assetId] || 'default';
};

export const getFolderColor = (assetId) => {
  const id = getFolderColorId(assetId);
  return FOLDER_COLORS.find((c) => c.id === id) || FOLDER_COLORS[0];
};

export const setFolderColor = (assetId, colorId) => {
  if (!assetId) return;
  const all = readAll();
  if (!colorId || colorId === 'default') {
    delete all[assetId];
  } else {
    all[assetId] = colorId;
  }
  writeAll(all);
};

const computeFolderColor = (assetId, fallbackIndex) => {
  const storedId = getFolderColorId(assetId);
  if (storedId && storedId !== 'default') {
    const c = FOLDER_COLORS.find((x) => x.id === storedId) || FOLDER_COLORS[0];
    return { ...c, isCustom: true };
  }
  const c = getDefaultColorForAsset(assetId, fallbackIndex);
  return { ...c, isCustom: false };
};

/**
 * React hook — subscribes to localStorage changes so any open card
 * re-renders when the color tag is updated.
 *
 * @param {string} assetId
 * @param {number} [fallbackIndex] — secondary seed used when assetId is missing.
 * Returns the resolved FOLDER_COLORS entry, plus { isCustom } flag.
 */
export const useFolderColor = (assetId, fallbackIndex) => {
  const [color, setColor] = useState(() => computeFolderColor(assetId, fallbackIndex));

  useEffect(() => {
    setColor(computeFolderColor(assetId, fallbackIndex));
    if (typeof window === 'undefined') return undefined;
    const handler = () => setColor(computeFolderColor(assetId, fallbackIndex));
    window.addEventListener('storage', handler);
    window.addEventListener(EVENT, handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener(EVENT, handler);
    };
  }, [assetId, fallbackIndex]);

  return color;
};
